// How Arlo is doing against the household's five goals (content/goals.ts),
// worked out from the last two weeks of logs. Every line of evidence says
// where it comes from (his logs, the camera, the socialisation checklist),
// and the thresholds are the app's rules of thumb — it says "not enough
// logged yet" rather than guessing.

import { GOALS, type GoalId } from '../content/goals.ts';
import type { SourceRef } from '../content/types.ts';
import { sleepTargetHours } from './age.ts';
import { DAY } from './time.ts';
import { DEFAULT_NIGHT, averageDailySleep } from './sleep.ts';
import type { LogEvent, Millis } from './types.ts';

export type GoalStatus = 'good' | 'building' | 'attention' | 'unknown';
export type EvidenceFrom = 'logs' | 'camera' | 'checklist';

export interface Evidence {
  text: string;
  from: EvidenceFrom;
}

export interface GoalCheck {
  id: GoalId;
  title: string;
  status: GoalStatus;
  /** One line: how it's going. */
  headline: string;
  evidence: Evidence[];
  /** The one thing to do next, and where in the app. */
  next: { text: string; route: string };
  /** Guidance behind the advice. */
  sources: SourceRef[];
}

export const FROM_LABEL: Record<EvidenceFrom, string> = { logs: 'Your logs', camera: 'Camera', checklist: 'Checklist' };

export interface GoalInput {
  events: LogEvent[];
  now: Millis;
  ageWeeks: number;
  /** Items on the socialisation checklist (content/socialisation.ts). */
  socialTotal: number;
  sessionsPerDay: number;
}

const trend = (cur: number, prev: number) => (cur === prev ? (cur ? 'same as the week before' : '') : cur < prev ? `down from ${prev} the week before` : `up from ${prev} the week before`);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function checkGoals(x: GoalInput): GoalCheck[] {
  const { events, now } = x;
  const weekAgo = now - 7 * DAY;
  const twoWeeks = now - 14 * DAY;
  const thisWeek = (e: LogEvent) => e.at > weekAgo && e.at <= now;
  const lastWeek = (e: LogEvent) => e.at > twoWeeks && e.at <= weekAgo;
  const recent = events.filter((e) => e.at > twoWeeks && e.at <= now);
  const anything = recent.some(thisWeek);
  // Days he's been logged this week (so a first week isn't judged against seven).
  const first = recent.reduce((m, e) => Math.min(m, e.at), now);
  const days = Math.max(1, Math.min(7, Math.ceil((now - Math.max(first, weekAgo)) / DAY)));

  const spells = (kinds: string[], when: (e: LogEvent) => boolean) => recent.filter((e) => e.type === 'behaviour' && kinds.includes(e.data.kind) && when(e)).length;
  const camera = (kinds: string[], when: (e: LogEvent) => boolean) => recent.filter((e) => e.type === 'camera' && kinds.includes(e.data.kind) && when(e)).length;
  const sessions = (skills?: string[]) => recent.filter((e) => e.type === 'training' && thisWeek(e) && (!skills || e.data.skills.some((k) => skills.includes(k)))).length;
  const hasCamera = events.some((e) => e.type === 'camera');
  const goal = (id: GoalId) => GOALS.find((g) => g.id === id)!;
  const unknown = (id: GoalId): GoalCheck => ({ id, title: goal(id).title, status: 'unknown', headline: 'Not enough logged yet to judge — log a few days and this fills in.', evidence: [], next: { text: 'Log naps, wees and any wild spells on Today', route: '/' }, sources: goal(id).sources });
  if (!anything) return GOALS.map((g) => unknown(g.id));

  const out: GoalCheck[] = [];

  // Well-behaved: practice, and the unwanted behaviours trending down.
  {
    const g = goal('manners');
    const done = sessions();
    const aim = x.sessionsPerDay * days;
    const cur = spells(g.behaviours, thisWeek);
    const prev = spells(g.behaviours, lastWeek);
    const top = topKind(recent.filter(thisWeek), g.behaviours);
    const status: GoalStatus = done < aim * 0.3 || cur >= prev + 3 ? 'attention' : done >= aim * 0.7 && cur <= prev ? 'good' : 'building';
    out.push({
      id: g.id,
      title: g.title,
      status,
      headline: status === 'good' ? 'Training most days, and the tricky spells aren’t growing.' : status === 'attention' ? (done < aim * 0.3 ? 'Not much training logged this week — a few 2-minute sessions a day is plenty.' : 'More bitey/jumpy spells than last week — usually tiredness.') : 'Getting there: keep the little-and-often sessions going.',
      evidence: [
        { text: `${plural(done, 'training session')} in ${plural(days, 'day')} (aim about ${aim})`, from: 'logs' },
        { text: `${plural(cur, 'manners spell')} logged (biting, jumping, stealing, chewing…)${trend(cur, prev) ? ` — ${trend(cur, prev)}` : ''}`, from: 'logs' },
      ],
      next: done < aim * 0.7 ? { text: 'Do a 2-minute session from this week’s plan', route: '/training' } : top ? { text: `What to do about ${top.replace('-', ' ')}`, route: `/training/help?b=${top}` } : { text: 'Play the four-paws game at the door', route: '/training/games?g=four-paws' },
      sources: g.sources,
    });
  }

  // No barking: logged barking and the camera's bark detections.
  {
    const g = goal('quiet');
    const cur = spells(['barking'], thisWeek) + camera(['bark'], thisWeek);
    const prev = spells(['barking'], lastWeek) + camera(['bark'], lastWeek);
    const status: GoalStatus = cur >= 7 || cur >= prev + 3 ? 'attention' : cur <= 2 ? 'good' : 'building';
    const evidence: Evidence[] = [{ text: `${plural(spells(['barking'], thisWeek), 'barking spell')} logged this week`, from: 'logs' }];
    if (hasCamera) evidence.push({ text: `${plural(camera(['bark'], thisWeek), 'bark detection')} from the camera${trend(camera(['bark'], thisWeek), camera(['bark'], lastWeek)) ? ` — ${trend(camera(['bark'], thisWeek), camera(['bark'], lastWeek))}` : ''}`, from: 'camera' });
    else evidence.push({ text: 'No camera connected, so only barking you log counts', from: 'camera' });
    out.push({
      id: g.id,
      title: g.title,
      status,
      headline: status === 'good' ? 'Quiet so far — now’s the time to make flat noises boring.' : status === 'attention' ? 'More barking than usual — note what set it off when you log it.' : 'A bit of barking — normal; reward the quiet moments.',
      evidence,
      next: cur > 0 ? { text: 'Why he barks and what helps', route: '/training/help?b=barking' } : { text: 'Practise flat noises (buzzer, lift, street) with treats', route: '/social' },
      sources: g.sources,
    });
  }

  // Comfortable: sleep, crying/clinginess, alone-time practice, handling.
  {
    const g = goal('comfortable');
    const [lo, hi] = sleepTargetHours(x.ageWeeks);
    const sleep = sleepPerDay(recent, now);
    const cur = spells(g.behaviours, thisWeek) + camera(['cry'], thisWeek);
    const prev = spells(g.behaviours, lastWeek) + camera(['cry'], lastWeek);
    const alone = recent.filter((e) => e.type === 'alone' && thisWeek(e)).length;
    const handled = sessions(['handling', 'crate-love']) + recent.filter((e) => e.type === 'groom' && thisWeek(e)).length;
    const lowSleep = sleep !== undefined && sleep < lo - 3;
    const status: GoalStatus = lowSleep || cur >= prev + 3 ? 'attention' : !lowSleep && cur <= prev && alone >= 3 ? 'good' : 'building';
    out.push({
      id: g.id,
      title: g.title,
      status,
      headline: lowSleep ? 'Short on sleep — the quickest win for a happier, calmer puppy.' : status === 'good' ? 'Sleeping well and practising time on his own.' : status === 'attention' ? 'More crying or clinginess than last week — go back a step with alone time.' : 'Building up: crate, alone time and gentle handling, little and often.',
      evidence: [
        sleep !== undefined ? { text: `About ${Math.round(sleep)} h of sleep a day logged lately (guidance for his age: ${lo}–${hi} h)`, from: 'logs' } : { text: 'Not enough naps logged to judge his sleep', from: 'logs' },
        { text: `${plural(cur, 'crying or clingy spell')}${hasCamera ? ' (logs + camera)' : ''}${trend(cur, prev) ? ` — ${trend(cur, prev)}` : ''}`, from: hasCamera ? 'camera' : 'logs' },
        { text: `${plural(alone, 'alone-time practice')} and ${plural(handled, 'crate or handling session')} this week`, from: 'logs' },
      ],
      next: lowSleep ? { text: 'Protect naps: about an hour awake, then crate and a chew', route: '/training/help?b=wont-settle' } : alone < 3 ? { text: 'Practise tiny separations (seconds, then minutes)', route: '/training/alone' } : { text: 'A minute of calm handling while he’s sleepy', route: '/training/games?g=calm-handling' },
      sources: g.sources,
    });
  }

  // Sociable: new experiences, how he took them, and the checklist.
  {
    const g = goal('sociable');
    const social = recent.filter((e): e is LogEvent<'social'> => e.type === 'social' && thisWeek(e));
    const worried = social.filter((e) => e.data.reaction === 'worried').length;
    const ticked = new Set(events.filter((e): e is LogEvent<'social'> => e.type === 'social').map((e) => e.data.itemId)).size;
    const inWindow = x.ageWeeks < 16;
    const aim = inWindow ? 7 : 3;
    const status: GoalStatus = (social.length >= 4 && worried / social.length >= 0.3) || (inWindow && social.length < 3) ? 'attention' : social.length >= aim && worried / Math.max(1, social.length) < 0.25 ? 'good' : 'building';
    const evidence: Evidence[] = [
      { text: `${plural(social.length, 'new experience')} this week${social.length ? ` — ${social.length - worried} happy or curious, ${worried} worried` : ''}`, from: 'logs' },
      { text: `${ticked} of ${x.socialTotal} socialisation checklist items done`, from: 'checklist' },
    ];
    if (inWindow) evidence.push({ text: `About ${16 - Math.floor(x.ageWeeks)} weeks left of the key socialisation window (Dogs Trust: about 3–16 weeks)`, from: 'logs' });
    out.push({
      id: g.id,
      title: g.title,
      status,
      headline: status === 'good' ? 'Lots of good new experiences, and he’s taking them well.' : status === 'attention' ? (social.length >= 4 && worried / social.length >= 0.3 ? 'He’s finding some things worrying — slow down, add distance and treats.' : 'Few new experiences logged — the window is open now; watching from your arms counts.') : 'Keep going: one or two short, happy new things a day.',
      evidence,
      next: { text: worried ? 'Go slower with the things that worried him' : 'Pick the next things from the socialisation checklist', route: '/social' },
      sources: g.sources,
    });
  }

  // Calm: wild spells trending down, and the settle being practised.
  {
    const g = goal('calm');
    const kinds = ['zoomies', 'wont-settle'];
    const cur = spells(kinds, thisWeek);
    const prev = spells(kinds, lastWeek);
    const settle = sessions(['settle', 'impulse-control', 'settle-out']);
    const status: GoalStatus = cur >= 10 || cur >= prev + 3 ? 'attention' : settle >= 3 && cur <= prev ? 'good' : 'building';
    out.push({
      id: g.id,
      title: g.title,
      status,
      headline: status === 'good' ? 'Practising his settle, and wild spells aren’t growing.' : status === 'attention' ? 'More wild or can’t-settle spells — nearly always missed naps.' : 'Teach the settle now — it’s the skill behind a calm adult dog.',
      evidence: [
        { text: `${plural(cur, 'zoomies or won’t-settle spell')}${trend(cur, prev) ? ` — ${trend(cur, prev)}` : ''}`, from: 'logs' },
        { text: `${plural(settle, 'settle or self-control session')} this week`, from: 'logs' },
      ],
      next: settle < 3 ? { text: 'Teach the settle on a mat', route: '/training/settle' } : { text: 'Play the off-switch game', route: '/training/games?g=off-switch' },
      sources: g.sources,
    });
  }

  return out;
}

/** Most-logged behaviour of these kinds. */
function topKind(events: LogEvent[], kinds: string[]): string | undefined {
  const n = new Map<string, number>();
  for (const e of events) if (e.type === 'behaviour' && kinds.includes(e.data.kind)) n.set(e.data.kind, (n.get(e.data.kind) ?? 0) + 1);
  return [...n.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
}

/** Average hours asleep per day over the last three whole days that have naps logged (domain/sleep.ts). */
export function sleepPerDay(events: LogEvent[], now: Millis): number | undefined {
  return averageDailySleep(events, now, 3, DEFAULT_NIGHT)?.hours;
}
